import { Pause, Play, Rewind, Rocket, SkipBack, SkipForward } from 'lucide-react';
import type { ManeuverDto } from '@trajectory/contracts';
import { useThrottledPlayhead } from '@/hooks/useThrottledPlayhead';
import { pause, play, seek, setSpeed } from '@/lib/playbackClock';
import { useMissionStore } from '@/stores/missionStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useTelemetryStore } from '@/stores/telemetryStore';

// See the matching comment in OrbitEditorPanel.tsx: this must be a stable
// reference, not a fresh `[]` literal returned from inside the selector.
const EMPTY_MANEUVERS: ManeuverDto[] = [];

const SPEEDS = [0.5, 1, 5, 20, 60, 300, 1200, 3600, 21600, 86400, 604800];

function formatT(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+';
  const abs = Math.abs(seconds);
  if (abs < 3600) return `T${sign}${Math.round(abs / 60)}m`;
  return `T${sign}${(abs / 3600).toFixed(1)}h`;
}

function formatClock(seconds: number): string {
  const total = Math.round(seconds);
  const days = Math.floor(total / 86400);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (days > 0) {
    return `${days}d ${(h % 24).toString().padStart(2, '0')}:${m
      .toString()
      .padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Static plan-time view: maneuvers laid out along the mission clock, before any simulation has been run. */
function PlannedTimeline() {
  const missionId = useMissionStore((state) => state.id);
  const maneuvers = useMissionStore((state) => state.document?.maneuvers ?? EMPTY_MANEUVERS);
  const selection = useSelectionStore((state) => state.selection);
  const select = useSelectionStore((state) => state.select);

  if (!missionId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-xs text-faint">
        No mission selected.
      </div>
    );
  }

  const maxTime = Math.max(60, ...maneuvers.map((maneuver) => maneuver.startTimeSeconds), 0) * 1.1;

  return (
    <div className="flex h-full flex-col justify-center gap-2 px-4 text-xs text-muted">
      <div className="relative h-10 rounded-control bg-surface-sunken">
        <div
          title="Liftoff"
          className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
          style={{ left: '0%' }}
        >
          <Rocket className="h-4 w-4 text-accent" />
        </div>
        {maneuvers.map((maneuver) => {
          const leftPercent = (maneuver.startTimeSeconds / maxTime) * 100;
          const isSelected = selection?.kind === 'maneuver' && selection.maneuverId === maneuver.id;
          return (
            <button
              key={maneuver.id}
              type="button"
              title={`${maneuver.name || maneuver.kind} · ${formatT(maneuver.startTimeSeconds)} · ${maneuver.deltaVMetersPerSecond.toFixed(0)} m/s`}
              onClick={() => select({ kind: 'maneuver', maneuverId: maneuver.id })}
              style={{ left: `${Math.min(98, leftPercent)}%` }}
              className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${
                isSelected ? 'border-accent bg-accent' : 'border-muted bg-surface'
              }`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-faint">
        <span>T+0</span>
        <span>{formatT(maxTime)}</span>
      </div>
      <p className="text-center text-faint">
        Run a simulation from the Telemetry panel to see a live flight timeline here.
      </p>
    </div>
  );
}

/** Live view once a simulation has completed: real flight events, transport controls and a scrubber. */
function LiveTimeline() {
  const events = useTelemetryStore((state) => state.events);
  const summary = useTelemetryStore((state) => state.summary);
  const { time, duration, playing, speed } = useThrottledPlayhead();

  if (!summary) return null;

  return (
    <div className="flex h-full flex-col justify-center gap-2 px-4 text-xs text-muted">
      <div className="relative h-10 rounded-control bg-surface-sunken">
        {events.map((event, index) => (
          <div
            key={`${event.type}-${index}`}
            title={`${event.label} · ${formatT(event.t)}${event.detail ? ` · ${event.detail}` : ''}`}
            style={{ left: `${duration > 0 ? Math.min(99, (event.t / duration) * 100) : 0}%` }}
            className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-info"
          />
        ))}
        <div
          style={{ left: `${duration > 0 ? Math.min(100, (time / duration) * 100) : 0}%` }}
          className="absolute top-0 h-full w-0.5 -translate-x-1/2 bg-accent"
        />
      </div>

      <input
        type="range"
        aria-label="Simulation playback position"
        min={0}
        max={duration || 1}
        step="any"
        value={time}
        onChange={(event) => seek(Number(event.target.value))}
        className="w-full accent-accent"
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => (playing ? pause() : play())}
          title={playing ? 'Pause simulation playback' : 'Play simulation playback'}
          aria-label={playing ? 'Pause simulation playback' : 'Play simulation playback'}
          className="flex h-6 w-6 items-center justify-center rounded-control border border-border text-text hover:bg-surface-sunken"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => {
            pause();
            seek(0);
          }}
          title="Rewind to launch"
          aria-label="Rewind to launch"
          className="flex h-6 w-6 items-center justify-center rounded-control border border-border text-text hover:bg-surface-sunken"
        >
          <Rewind className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            pause();
            seek(time - 1);
          }}
          title="Step backward one second"
          aria-label="Step backward one second"
          className="flex h-6 w-6 items-center justify-center rounded-control border border-border text-text hover:bg-surface-sunken"
        >
          <SkipBack className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            pause();
            seek(time + 1);
          }}
          title="Step forward one second"
          aria-label="Step forward one second"
          className="flex h-6 w-6 items-center justify-center rounded-control border border-border text-text hover:bg-surface-sunken"
        >
          <SkipForward className="h-3.5 w-3.5" />
        </button>
        <span className="tabular-nums text-text">{formatClock(time)}</span>
        <span className="text-faint">/ {formatClock(duration)}</span>
        <div className="flex-1" />
        <label className="flex items-center gap-1">
          Speed
          {/* Controlled from the clock, not `defaultValue`: the clock's speed
              outlives this component, so an uncontrolled select silently
              claimed 1× again every time a new run remounted the timeline
              while playback actually stayed at whatever rate was last set. */}
          <select
            value={speed}
            onChange={(event) => setSpeed(Number(event.target.value))}
            className="rounded-control border border-border bg-surface px-1 py-0.5 text-text"
          >
            {SPEEDS.map((speed) => (
              <option key={speed} value={speed}>
                {speed}×
              </option>
            ))}
          </select>
        </label>
        <span
          className={
            summary.reachedDestination || summary.reachedOrbit
              ? 'text-success'
              : summary.impacted
                ? 'text-danger'
                : 'text-warning'
          }
        >
          {summary.reachedDestination
            ? summary.capturedAtDestination
              ? `Captured at ${summary.destinationBodyId ?? 'destination'}`
              : `Reached ${summary.destinationBodyId ?? 'destination'}`
            : summary.reachedOrbit
              ? 'Reached parking orbit'
            : summary.impacted
              ? 'Impacted'
              : summary.terminationReason}
        </span>
      </div>
    </div>
  );
}

export function TimelinePanel() {
  const status = useTelemetryStore((state) => state.status);
  return status === 'ready' ? <LiveTimeline /> : <PlannedTimeline />;
}
